import { Controller } from '@hotwired/stimulus'

const STORAGE_KEY = 'assistant-chat-open'

// I-UX-CHAT-01 floating assistant chat (layouts/application): toggles the panel, remembers open/closed per tab
// (sessionStorage), Esc closes and returns focus to the button, Enter sends (Shift+Enter = new line).
export default class AssistantChatController extends Controller<HTMLElement> {
  static override targets = ['panel', 'button', 'frame']

  declare readonly panelTarget: HTMLElement
  declare readonly buttonTarget: HTMLButtonElement
  declare readonly frameTarget: HTMLElement

  // I-AI-03: the panel is permanent across Turbo visits, so every visit re-points its frame at the page's order
  // (<body data-assistant-order-id>) for the prompt chips. A lazy frame not yet shown only takes the new src,
  // a loaded one reloads (Turbo FrameController).
  private readonly syncContext = (): void => {
    const url = new URL(this.frameTarget.getAttribute('src') ?? '', window.location.href)
    const orderId = document.body.dataset['assistantOrderId'] ?? ''
    if ((url.searchParams.get('order_id') ?? '') === orderId) return

    if (orderId) url.searchParams.set('order_id', orderId)
    else url.searchParams.delete('order_id')
    this.frameTarget.setAttribute('src', url.pathname + url.search)
  }

  override connect(): void {
    this.syncContext()
    document.addEventListener('turbo:load', this.syncContext)
    if (readOpen() && this.panelTarget.hidden) this.show(false)
  }

  override disconnect(): void {
    document.removeEventListener('turbo:load', this.syncContext)
  }

  // A prompt chip only fills the message box; sending stays with the user.
  suggest(event: Event): void {
    const prompt = (event.currentTarget as HTMLElement).dataset['prompt']
    const input = this.panelTarget.querySelector<HTMLTextAreaElement>('textarea')
    if (!prompt || !input) return

    input.value = prompt
    input.focus()
  }

  toggle(): void {
    if (this.panelTarget.hidden) this.show(true)
    else this.hide()
  }

  hide(): void {
    this.panelTarget.hidden = true
    this.buttonTarget.hidden = false
    this.buttonTarget.setAttribute('aria-expanded', 'false')
    writeOpen(false)
    this.buttonTarget.focus()
  }

  loaded(): void {
    this.scrollToEnd()
    if (!this.panelTarget.hidden) this.focusInput()
  }

  keydown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && !this.panelTarget.hidden) {
      event.stopPropagation()
      this.hide()
    } else if (event.key === 'Enter' && !event.shiftKey && !event.isComposing && event.target instanceof HTMLTextAreaElement) {
      event.preventDefault()
      if (event.target.value.trim()) event.target.form?.requestSubmit()
    }
  }

  private show(focus: boolean): void {
    this.panelTarget.hidden = false
    this.buttonTarget.setAttribute('aria-expanded', 'true')
    // A full-screen sheet below sm covers the button; it closes from the panel header.
    this.buttonTarget.hidden = matchMedia('(max-width: 639px)').matches
    writeOpen(true)
    this.scrollToEnd()
    if (focus) this.focusInput()
  }

  private focusInput(): void {
    this.panelTarget.querySelector<HTMLTextAreaElement>('textarea')?.focus()
  }

  private scrollToEnd(): void {
    const scroll = this.panelTarget.querySelector<HTMLElement>('[data-assistant-chat-target="scroll"]')
    if (scroll) scroll.scrollTop = scroll.scrollHeight
  }
}

function readOpen(): boolean {
  try { return sessionStorage.getItem(STORAGE_KEY) === '1' } catch { return false }
}

function writeOpen(open: boolean): void {
  try { sessionStorage.setItem(STORAGE_KEY, open ? '1' : '0') } catch { /* storage unavailable */ }
}
